import { Component, QueryList, ViewChildren } from '@angular/core';
import { DataService } from '../../../services/data/data.service';
import { TranslateService } from '@ngx-translate/core';
import { typesApex, FiltersM } from '../../../components/models';
import { Meses } from '../../../models/Formulacion/indicadores-metas/datos';
import { GraficadorComponent } from '../../../components/graficador/graficador.component';
import { Aligment, ExcelService, FormatText } from '../../../services/excel/excel.service';
import {
  AnonymousResultPeriod,
  AnonymousResults,
  AnonymousResultCategory,
  FiltroDemografico,
  ResultadosClimaService,
} from '../../../services/cuestionarios/resultados-clima/resultados-clima.service';

interface ValorEje {
  value: string;
  count: number;
}

interface FiltroActivo {
  eje: string;
  valor: string;
}

/** Chip mostrado; eje null identifica el chip de Dimensión, que solo redibuja y no se manda al back */
interface ChipFiltro {
  eje: string | null;
  texto: string;
}

/** Rango de años con ambos extremos incluidos */
interface RangoEdad {
  min: number;
  max: number;
}

/** Estado de cada gráfica de la pantalla: tipo actual, etiquetas, modal y tipos disponibles en su menú. */
interface GraficoPerfil {
  zona: number;
  titulo: string;
  /** Eje demográfico que filtra al hacer clic; null si la gráfica no filtra */
  eje: string | null;
  type: string;
  tipoInicial: string;
  etiquetas: boolean;
  ampliado: boolean;
  tipos: string[];
}

/** Datos de una gráfica en un tipo: colores por categoría para barras, pie, embudo y mapa de calor */
interface DatosGrafico {
  data: any[];
  colores: string[];
}

/** Datos de una zona. `combo` solo existe donde la gráfica original es combinada */
interface DatosZona {
  labels: string[];
  simple: DatosGrafico;
  combo?: DatosGrafico;
  axisY: string[];
  axisYCombo?: string[];
}

/** Tabla de una gráfica: conteo y % por categoría */
interface TablaPerfil {
  name: string;
  valores: number[];
  porcentaje: number[];
  total: number;
}

@Component({
  selector: 'app-perfil',
  templateUrl: './perfil.component.html',
  styleUrl: './perfil.component.css'
})
export class PerfilComponent {
  public types = typesApex;
  public Filters = FiltersM;

  /** Orden de los tipos en el submenú «Gráficas», con su icono y texto. */
  private readonly ordenTipos: string[] = [typesApex.BAR, typesApex.LINE, typesApex.PIE, typesApex.FUNNEL, typesApex.HEATMAP, typesApex.COMBO, typesApex.TABLE];
  public readonly infoTipos: Record<string, { icono: string; texto: string }> = {
    [typesApex.BAR]: { icono: 'bar_chart', texto: 'graf.barras' },
    [typesApex.LINE]: { icono: 'show_chart', texto: 'graf.lineal' },
    [typesApex.PIE]: { icono: 'pie_chart', texto: 'graf.pie' },
    [typesApex.FUNNEL]: { icono: 'change_history', texto: 'graf.funnel' },
    [typesApex.HEATMAP]: { icono: 'view_quilt', texto: 'graf.calor' },
    [typesApex.COMBO]: { icono: 'multiline_chart', texto: 'graf.combinada' },
    [typesApex.TABLE]: { icono: 'table_chart', texto: 'graf.tabla' },
  };


  private static readonly EJE_GENERO = 'gender';
  private static readonly EJE_EDAD = 'age-range';
  private static readonly EJE_ANTIGUEDAD = 'seniority-range';
  private static readonly EJE_FORMACION = 'academic-education-level';
  private static readonly EJE_JERARQUIA = 'hierarchy-level';
  private static readonly EJE_SUCURSAL = 'branch-office';

  /** Ejes clickeables desde la plantilla. Universo y Generación no filtran */
  public readonly ejes = {
    genero: PerfilComponent.EJE_GENERO,
    edad: PerfilComponent.EJE_EDAD,
    antiguedad: PerfilComponent.EJE_ANTIGUEDAD,
    formacion: PerfilComponent.EJE_FORMACION,
    jerarquia: PerfilComponent.EJE_JERARQUIA,
  };

  /** Placeholders que NO cuentan como valor real de un eje demográfico */
  private static readonly PLACEHOLDERS = ['No especificado', 'Sin dato'];

  /** Cohortes generacionales por año de nacimiento (límites inclusivos) */
  private static readonly GENERACIONES = [
    { clave: 'gen-z', desde: 1997, hasta: 2012 },
    { clave: 'gen-y', desde: 1981, hasta: 1996 },
    { clave: 'gen-x', desde: 1965, hasta: 1980 },
    { clave: 'gen-boomers', desde: 1946, hasta: 1964 },
    { clave: 'gen-silenciosa', desde: 1928, hasta: 1945 },
  ];

  /** Edad tope al interpretar un rango abierto ("66+") */
  private static readonly EDAD_MAXIMA = 100;

  private static readonly PALETA = ['#5EAEE5', '#8AD2E6', '#A4D55D', '#FFE370', '#FFAE5F', '#FB7598', '#B7B7B7', '#7C8FD6'];
  /** Rebanadas no elegidas de un eje filtrado */
  private static readonly COLOR_APAGADO = '#DCDCDC';
  private static readonly COLOR_BARRA = '#4472c4';
  private static readonly COLOR_LINEA = '#ed7d31';

  cuestionario_name = '';
  private institutionId = '';
  private questionnaireId = '';
  private allocationSelect: number | null = null;
  private periodosPropios: AnonymousResultPeriod[] = [];
  /** Población filtrada según filtrosActivos; sin filtros, es igual a resultadoBase */
  private resultado: AnonymousResults | null = null;
  /** Población completa del periodo: alimenta los catálogos de filtros */
  private resultadoBase: AnonymousResults | null = null;
  /**
   * Resultado por combinación de filtros ya pedida. aplicarFiltros() repite combos
   * entre llamadas (quitar un chip suele reusar lo que antes era "el eje sin su propio
   * filtro"), así que esto evita ida y vuelta al back aunque ya responda rápido por su cache.
   */
  private cacheResultados = new Map<string, AnonymousResults | null>();
  /** Contador para descartar respuestas que un click posterior dejó obsoletas */
  private ultimaPeticion = 0;
  /** Color fijo por categoría en cada eje: evita que se corran los colores cuando una categoría desaparece */
  private coloresPorEje = new Map<string, Map<string, string>>();

  public loadingResultados = false;
  public periodos: any[] = [];
  public periodoSelect = '';

  /** Filtros activos, máximo uno por eje; se acumulan con AND */
  public filtrosActivos: FiltroActivo[] = [];

  public sucursales: { name: string; check: boolean }[] = [];
  public sucursalConsolidado = true;
  public sucursalesDisponibles = false;

  public dimensiones: { name: string; check: boolean }[] = [];
  public dimensionConsolidado = true;

  // Pastel: Universo vs muestra (universo = encuestas enviadas del periodo)
  public labelsUniverso: string[] = []; public dataUniverso: any[] = []; public coloresUniverso: string[] = [];

  // Donas: Género, Edad
  public labelsGenero: string[] = []; public dataGenero: any[] = []; public coloresGenero: string[] = [];
  public labelsEdad: string[] = []; public dataEdad: any[] = []; public coloresEdad: string[] = [];

  // Escalón (funnel): Generación, derivada de los rangos de edad
  public labelsGeneracion: string[] = []; public dataGeneracion: any[] = []; public coloresGeneracion: string[] = [];

  // Treemap: Antigüedad (% de la muestra por rango)
  public labelsAntiguedad: string[] = []; public dataAntiguedad: any[] = []; public coloresAntiguedad: string[] = [];

  // Combo: Nivel jerárquico (línea = colaboradores, barra = participación %)
  public labelsJerarquia: string[] = []; public dataJerarquia: any[] = [];
  public coloresCombo = [PerfilComponent.COLOR_LINEA, PerfilComponent.COLOR_BARRA];

  // Barra horizontal: Formación académica (% de la muestra por nivel)
  public labelsFormacion: string[] = []; public dataFormacion: any[] = []; public coloresFormacion: string[] = [];

  // Cards de la muestra
  public cardUniverso = 0;
  public cardMuestra = 0;
  public cardMuestraPct = 0;
  public cardPromedioEdad: number | null = null;
  public cardPromedioAntiguedad: number | null = null;
  public cardFormacion: { name: string; pct: number } | null = null;
  public cardJerarquia: { name: string; pct: number } | null = null;
  public cardSucursal: { name: string; pct: number } | null = null;
  public cardGenero: { name: string; pct: number } | null = null;

  /** Cada gráfica con el tipo con el que abre y los que puede mostrar además de la tabla. */
  private crearGrafico(zona: number, titulo: string, eje: string | null, type: string, tipos: string[]): GraficoPerfil {
    return {
      zona,
      titulo: 'cues.perfil-clima.' + titulo,
      eje,
      type,
      tipoInicial: type,
      // La combinada de jerarquía abre sin etiquetas, como antes: con dos series de escalas distintas se amontonan
      etiquetas: type !== typesApex.COMBO,
      ampliado: false,
      tipos: this.ordenTipos.filter(t => tipos.includes(t) || t === typesApex.TABLE),
    };
  }
  private static readonly TIPOS_BASE = [typesApex.BAR, typesApex.LINE, typesApex.PIE, typesApex.FUNNEL];
  public graficos: GraficoPerfil[] = [
    this.crearGrafico(1, 'titulo-universo', null, typesApex.PIE, PerfilComponent.TIPOS_BASE),
    this.crearGrafico(2, 'titulo-genero', PerfilComponent.EJE_GENERO, typesApex.PIE, PerfilComponent.TIPOS_BASE),
    this.crearGrafico(3, 'titulo-edad', PerfilComponent.EJE_EDAD, typesApex.PIE, PerfilComponent.TIPOS_BASE),
    this.crearGrafico(4, 'titulo-generacion', null, typesApex.FUNNEL, PerfilComponent.TIPOS_BASE),
    this.crearGrafico(5, 'titulo-antiguedad', PerfilComponent.EJE_ANTIGUEDAD, typesApex.HEATMAP, [...PerfilComponent.TIPOS_BASE, typesApex.HEATMAP]),
    this.crearGrafico(6, 'titulo-jerarquico', PerfilComponent.EJE_JERARQUIA, typesApex.COMBO, [...PerfilComponent.TIPOS_BASE, typesApex.COMBO]),
    this.crearGrafico(7, 'titulo-formacion', PerfilComponent.EJE_FORMACION, typesApex.FUNNEL, PerfilComponent.TIPOS_BASE),
  ];
  /** Dos filas, como se acomodan en pantalla: cuatro arriba y tres abajo. */
  public filasGraficos: GraficoPerfil[][] = [this.graficos.slice(0, 4), this.graficos.slice(4, 7)];

  /** Datos por zona (índice = zona). Se arman al recalcular y se reusan: si cambiaran en cada ciclo, el graficador se redibujaría sin parar */
  private datosPorZona: DatosZona[] = [];
  private tablaPorZona: TablaPerfil[] = [];
  public readonly coloresLinea = [PerfilComponent.COLOR_BARRA];
  public readonly leyendaVisible = { enabled: true, position: 'bottom' };
  public readonly leyendaOculta = { enabled: false, position: 'bottom' };
  private readonly datosVacios: DatosZona = { labels: [], simple: { data: [], colores: [] }, axisY: [''] };

  @ViewChildren(GraficadorComponent) grafComp!: QueryList<GraficadorComponent>;

  constructor(
    public dataService: DataService,
    private translate: TranslateService,
    private resultadosService: ResultadosClimaService,
    private excel: ExcelService,
  ) {
    this.getData();
    this.cargarPeriodos();
  }

  /** Cuestionario activo (localStorage): nombre para títulos */
  private getData(): void {
    const cues = JSON.parse(localStorage.getItem('cuestionario') || 'null');
    this.cuestionario_name = cues?.name ?? '';
  }

  private async cargarPeriodos(): Promise<void> {
    const cues = JSON.parse(localStorage.getItem('cuestionario') || 'null');
    const allocation = JSON.parse(localStorage.getItem('allocation') || 'null');
    this.questionnaireId = cues?.id != null ? String(cues.id) : '';
    this.institutionId = allocation?.['institution_id'] ?? '';

    if (!this.institutionId) {
      this.periodos = [];
      return;
    }

    let periods: AnonymousResultPeriod[] = [];
    try {
      periods = await this.resultadosService.getPeriods(this.institutionId);
    } catch {
      this.periodos = [];
      return;
    }

    const propios = (periods || []).filter(p => String(p.questionnaireId) === this.questionnaireId);
    this.periodosPropios = [...propios].sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));
    this.construirFiltroPeriodos(propios);

    // El back viene ordenado por startDate desc -> el primero es el más reciente
    const reciente = propios[0];
    if (reciente) {
      this.allocationSelect = reciente.allocationId;
      this.periodoSelect = reciente.startDate;
      this.marcarPeriodo(reciente.allocationId);
      await this.cargarResultados(reciente.allocationId);
    } else {
      this.allocationSelect = null;
      this.resultado = null;
      this.poblarFiltros();
      this.recomputar();
    }
  }

  /** Arma la estructura (año → series de meses) que consume app-filters PERIOD */
  private construirFiltroPeriodos(periods: AnonymousResultPeriod[]): void {
    const porAnio = new Map<string, any>();
    for (const p of periods) {
      const [y, m] = (p.startDate || '').split('-');
      if (!y || !m) continue;
      if (!porAnio.has(y)) {
        porAnio.set(y, { anio: Number(y), visible: true, utilizable: true, series: [] });
      }
      const mesKey = Meses.getMesesNumbertoString(Number(m), true);
      porAnio.get(y).series.push({
        mes: mesKey,
        mesS: mesKey,
        anio: y.substring(2),
        label: `${mesKey} ${y.substring(2)}`,
        periodo: String(p.allocationId),
        allocationId: p.allocationId,
        startDate: p.startDate,
        check: false,
        visible: true,
        utilizable: true,
      });
    }
    const arr = Array.from(porAnio.values()).sort((a, b) => a.anio - b.anio);
    arr.forEach(a => a.series.sort((x: any, y: any) => x.startDate.localeCompare(y.startDate)));
    this.periodos = arr;
  }

  private marcarPeriodo(allocationId: number): void {
    this.periodos.forEach(anio => {
      anio.series.forEach((per: any) => (per.check = per.allocationId === allocationId));
    });
  }

  public async yearChange(event: any): Promise<void> {
    const allocationId = event?.allocationId;
    if (allocationId == null) return;

    this.allocationSelect = allocationId;
    this.periodoSelect = event.startDate ?? this.periodoSelect;
    this.marcarPeriodo(allocationId);
    await this.cargarResultados(allocationId);
  }

  /** Cambio de periodo: se pide la población completa y se sueltan los filtros */
  private async cargarResultados(allocationId: number): Promise<void> {
    this.loadingResultados = true;
    try {
      this.filtrosActivos = [];
      this.cacheResultados.clear();
      this.ultimaPeticion++;   // invalida respuestas de filtros del periodo anterior
      this.resultadoBase = await this.pedirResultados(allocationId, []);
      this.resultado = this.resultadoBase;
      this.asignarColores();
      this.poblarFiltros();
      this.recomputar();
    } finally {
      this.loadingResultados = false;
    }
  }

  private async pedirResultados(allocationId: number, filtros: FiltroDemografico[]): Promise<AnonymousResults | null> {
    try {
      return await this.resultadosService.getResults(allocationId, this.institutionId, filtros);
    } catch {
      return null;
    }
  }

  /**
   * Fija el color de cada categoría con la población completa. Al filtrar, las
   * categorías que se quedan sin gente desaparecen pero las demás conservan el suyo.
   */
  private asignarColores(): void {
    this.coloresPorEje.clear();

    for (const eje of this.resultadoBase?.demographics ?? []) {
      const valores = this.sinPlaceholders(eje.values.map(v => ({ value: v.value, count: v.count })));
      const mapa = new Map<string, string>();
      valores.forEach((v, i) => mapa.set(v.value, PerfilComponent.PALETA[i % PerfilComponent.PALETA.length]));
      this.coloresPorEje.set(eje.key, mapa);
    }
  }

  /** Los catálogos de sucursal y dimensión salen de la población completa, no de la ya filtrada */
  private poblarFiltros(): void {
    const reales = (this.resultadoBase?.demographics?.find(d => d.key === PerfilComponent.EJE_SUCURSAL)?.values ?? [])
      .filter(v => v.count > 0 && !PerfilComponent.PLACEHOLDERS.includes(v.value));

    this.sucursalesDisponibles = reales.length > 0;
    this.sucursales = reales.map(v => ({ name: v.value, check: false }));

    this.dimensiones = this.padresUnicos(this.resultadoBase).map(n => ({ name: n, check: false }));
    this.dimensionConsolidado = true;
    this.sincronizarPills();
  }

  // --- Helpers de lectura del resultado ---

  private padresUnicos(res: AnonymousResults | null): string[] {
    const nombres = (res?.categories ?? [])
      .map(c => (c.parentCategoryName || '').trim())
      .filter(n => !!n);
    return Array.from(new Set(nombres));
  }

  private hijasDe(res: AnonymousResults | null, padre: string): AnonymousResultCategory[] {
    return (res?.categories ?? []).filter(c => (c.parentCategoryName || '').trim() === padre);
  }

  private catalogoLabel(key: string, fallback: string): string {
    const ruta = 'cues.cuestionariov2.catalogos.' + key;
    const val = this.translate.instant(ruta);
    return val !== ruta ? val : (fallback || key);
  }

  private etiquetaPeriodo(startDate: string): string {
    const [y, m] = (startDate || '').split('-');
    if (!y || !m) return '';
    const mes = Meses.getMesesNumbertoString(Number(m), true);
    return `${mes} ${y.substring(2)}`;
  }

  public get periodoTitulo(): string {
    return this.etiquetaPeriodo(this.periodoSelect);
  }

  /** Valores filtrados y/o dimensión unidos por " | "; "Consolidado" si no hay selección */
  public get segmentoSeleccion(): string {
    const partes = this.filtrosActivos.map(f => f.valor);
    if (!this.dimensionConsolidado) {
      const d = this.dimensiones.find(x => x.check);
      if (d) partes.push(d.name);
    }
    return partes.length ? partes.join(' | ') : this.translate.instant('cues.resultados-clima.titulo-zon1-1');
  }

  // --- Filtros demográficos ---

  /** Valor filtrado en un eje, o undefined si ese eje no está filtrado */
  public filtroDe(eje: string): string | undefined {
    return this.filtrosActivos.find(f => f.eje === eje)?.valor;
  }

  /**
   * Click en una rebanada. Se traduce el índice al valor crudo del eje en vez de
   * usar el texto de la etiqueta, que va traducido y puede no coincidir.
   */
  public async clickGrafica(eje: string, event: any): Promise<void> {
    const valor = this.valoresGrafica(eje)[event?.posValue]?.value;
    if (valor) await this.alternarFiltro(eje, valor);
  }

  /** Fija eje=valor; si ya estaba ese mismo valor lo quita, y si había otro lo reemplaza */
  public async alternarFiltro(eje: string, valor: string): Promise<void> {
    const anterior = this.filtroDe(eje);
    this.filtrosActivos = this.filtrosActivos.filter(f => f.eje !== eje);
    if (anterior !== valor) this.filtrosActivos.push({ eje, valor });
    await this.aplicarFiltros();
  }

  /** Chips a mostrar: los filtros activos en el orden en que se aplicaron, más el de Dimensión si hay una seleccionada */
  public get chips(): ChipFiltro[] {
    const activos: ChipFiltro[] = this.filtrosActivos.map(f => ({
      eje: f.eje,
      texto: `${this.catalogoLabel(f.eje, f.eje)}: ${f.valor}`,
    }));

    const dim = this.dimensionConsolidado ? undefined : this.dimensiones.find(d => d.check);
    if (dim) {
      activos.push({
        eje: null,
        texto: `${this.translate.instant('filtros.dimensiones')}: ${dim.name}`,
      });
    }
    return activos;
  }

  /** Quita un solo filtro; el chip de Dimensión no toca el back, solo redibuja */
  public async quitarChip(chip: ChipFiltro): Promise<void> {
    if (chip.eje === null) {
      this.dimensiones.forEach(d => d.check = false);
      this.dimensionConsolidado = true;
      this.recomputar();
      return;
    }

    this.filtrosActivos = this.filtrosActivos.filter(f => f.eje !== chip.eje);
    await this.aplicarFiltros();
  }

  /** Limpia filtrosActivos y también la dimensión, aunque el nombre solo hable de filtros */
  public async borrarFiltros(): Promise<void> {
    this.graficos.forEach(g => { g.type = g.tipoInicial; g.etiquetas = g.tipoInicial !== typesApex.COMBO; });
    this.dimensiones.forEach(d => d.check = false);
    this.dimensionConsolidado = true;
    this.filtrosActivos = [];
    await this.aplicarFiltros();
  }

  private async aplicarFiltros(): Promise<void> {
    if (this.allocationSelect == null) return;

    const allocationId = this.allocationSelect;
    const filtros = this.comoFiltrosDemograficos();
    const peticion = ++this.ultimaPeticion;

    this.loadingResultados = true;
    try {
      const completo = await this.pedirConFiltros(allocationId, filtros);

      // Otro click ya pidió algo distinto: esta respuesta llegó tarde y se tira
      if (peticion !== this.ultimaPeticion) return;

      this.resultado = completo;
      this.sincronizarPills();
      this.recomputar();
    } finally {
      if (peticion === this.ultimaPeticion) this.loadingResultados = false;
    }
  }

  /** Combinación de filtros, en un orden estable, para usarla como llave de cache */
  private claveFiltros(filtros: FiltroDemografico[]): string {
    return filtros
      .map(f => `${f.key}=${[...f.values].sort().join(',')}`)
      .sort()
      .join('&');
  }

  /**
   * Sin filtros no hay que ir al back: la población completa ya está cargada.
   * Si ya se pidió esta misma combinación en una llamada anterior a aplicarFiltros
   * (ej. quitar y volver a poner el mismo filtro), se reusa en vez de volver a pedirla.
   */
  private async pedirConFiltros(allocationId: number, filtros: FiltroDemografico[]): Promise<AnonymousResults | null> {
    if (!filtros.length) return this.resultadoBase;

    const clave = this.claveFiltros(filtros);
    if (this.cacheResultados.has(clave)) return this.cacheResultados.get(clave)!;

    const resultado = await this.pedirResultados(allocationId, filtros);
    // No se cachea un fallo: si la petición falló por algo transitorio, se reintenta la próxima vez
    if (resultado !== null) this.cacheResultados.set(clave, resultado);
    return resultado;
  }

  private comoFiltrosDemograficos(): FiltroDemografico[] {
    return this.filtrosActivos.map(f => ({ key: f.eje, values: [f.valor] }));
  }

  /** Las pills reflejan filtrosActivos; el estado no vive en ellas */
  private sincronizarPills(): void {
    const sucursal = this.filtroDe(PerfilComponent.EJE_SUCURSAL);
    this.sucursales.forEach(s => s.check = s.name === sucursal);
    this.sucursalConsolidado = sucursal === undefined;
  }

  /** Valores (value + count) de un eje, sobre una población que ya viene filtrada por el back */
  private valoresEje(eje: string, res: AnonymousResults | null): ValorEje[] {
    if (!res) return [];

    const dim = this.dimensiones.find(d => d.check);
    if (dim) {
      // Misma población en todas las categorías hijas de la dimensión: se toma la primera como representativa
      const representativa = this.hijasDe(res, dim.name)[0];
      const breakdown = representativa?.demographics?.find(d2 => d2.key === eje);
      return (breakdown?.values ?? []).map(v => ({ value: v.value, count: v.count }));
    }

    const breakdown = res.demographics?.find(d2 => d2.key === eje);
    return (breakdown?.values ?? []).map(v => ({ value: v.value, count: v.count }));
  }

  private sinPlaceholders(valores: ValorEje[]): ValorEje[] {
    return valores.filter(v => v.count > 0 && !PerfilComponent.PLACEHOLDERS.includes(v.value));
  }

  /** Valores dibujables de un eje: sin placeholders, sobre la población ya filtrada */
  private valoresGrafica(eje: string): ValorEje[] {
    return this.sinPlaceholders(this.valoresEje(eje, this.resultado));
  }

  /** Con el eje filtrado, solo la rebanada elegida conserva color */
  private colorear(eje: string, valores: ValorEje[]): string[] {
    const activo = this.filtroDe(eje);
    const mapa = this.coloresPorEje.get(eje);
    return valores.map((v, i) =>
      activo && v.value !== activo
        ? PerfilComponent.COLOR_APAGADO
        // El fallback cubre categorías que solo existen bajo una dimensión
        : mapa?.get(v.value) ?? PerfilComponent.PALETA[i % PerfilComponent.PALETA.length]
    );
  }

  private construirDonut(eje: string): { labels: string[]; data: any[]; colores: string[] } {
    const valores = this.valoresGrafica(eje);
    return {
      labels: valores.map(v => v.value),
      data: [{ name: this.catalogoLabel(eje, eje), data: valores.map(v => v.count) }],
      colores: this.colorear(eje, valores),
    };
  }

  /**
   * Serie única en CONTEO (para treemap y embudo). En porcentaje del total visible
   * las barras crecían al filtrar, porque se renormalizaban sobre menos gente.
   */
  private construirSerieUnica(eje: string): { labels: string[]; data: any[]; colores: string[] } {
    const valores = this.valoresGrafica(eje);
    return {
      labels: valores.map(v => v.value),
      data: [{
        name: this.catalogoLabel(eje, eje),
        data: valores.map(v => v.count),
      }],
      colores: this.colorear(eje, valores),
    };
  }

  /**
   * Sin apagado por categoría: el COMBO colorea por serie y el formato {x,y}
   * que lo permitiría deja la gráfica en blanco. El chip carga ese feedback.
   */
  private construirCombo(eje: string): { labels: string[]; data: any[] } {
    const valores = this.valoresGrafica(eje);
    const total = valores.reduce((a, v) => a + v.count, 0);
    return {
      labels: valores.map(v => v.value),
      data: [
        { name: this.translate.instant('cues.perfil-clima.serie-colaboradores'), data: valores.map(v => v.count) },
        { name: this.translate.instant('cues.perfil-clima.serie-participacion'), data: valores.map(v => total > 0 ? Number((v.count / total * 100).toFixed(1)) : 0) },
      ],
    };
  }

  /**
   * Pastel Universo vs muestra. El universo son las encuestas enviadas del
   * periodo y es fijo, así que al filtrar solo se achica la muestra.
   * Depende de cardMuestra, así que se construye después de construirCards().
   */
  private construirUniverso(): { labels: string[]; data: any[]; colores: string[] } {
    const resto = Math.max(this.cardUniverso - this.cardMuestra, 0);
    return {
      labels: [
        this.translate.instant('cues.perfil-clima.serie-muestra'),
        this.translate.instant('cues.perfil-clima.serie-resto'),
      ],
      data: [{
        name: this.translate.instant('cues.perfil-clima.footer-colaboradores'),
        data: [this.cardMuestra, resto],
      }],
      colores: [PerfilComponent.PALETA[0], '#DCDCDC'],
    };
  }

  /**
   * Traduce el texto de un bucket de edad a un rango de años.
   * Formas aceptadas: "18 - 25", "18 a 25", "66+", "+66" y "< 20".
   */
  private static rangoEdad(texto: string): RangoEdad | null {
    const valor = (texto || '').trim();
    const numeros = (valor.match(/\d+/g) ?? []).map(Number);

    if (numeros.length >= 2) return { min: numeros[0], max: numeros[1] };
    if (numeros.length === 0) return null;

    if (valor.startsWith('<')) return { min: 0, max: numeros[0] };
    if (valor.includes('+')) return { min: numeros[0], max: PerfilComponent.EDAD_MAXIMA };

    return null;
  }

  /** Cuántos años abarca un rango (ambos extremos incluidos) */
  private static anios(rango: RangoEdad): number {
    return rango.max - rango.min + 1;
  }

  /** Años que dos rangos tienen en común; 0 si no se tocan */
  private static aniosEnComun(a: RangoEdad, b: RangoEdad): number {
    const inicio = Math.max(a.min, b.min);
    const fin = Math.min(a.max, b.max);
    return Math.max(fin - inicio + 1, 0);
  }

  /** La cohorte, expresada como rango de edades para el año dado */
  private static edadesDeLaCohorte(cohorte: { desde: number; hasta: number }, anio: number): RangoEdad {
    return { min: anio - cohorte.hasta, max: anio - cohorte.desde };
  }

  /**
   * Reparte la muestra entre cohortes generacionales a partir del eje de edad.
   * Los buckets del cuestionario no coinciden con los límites de generación
   * (26-35 cae a caballo entre Z y millennials), así que cada bucket aporta a
   * cada cohorte la fracción de sus años que caen dentro de ella: es una
   * ESTIMACIÓN, no un conteo real. Se reemplaza si el back expone el eje.
   */
  private construirGeneracion(): { labels: string[]; data: any[]; colores: string[] } {
    const anio = new Date().getFullYear();

    const buckets = this.sinPlaceholders(this.valoresEje(PerfilComponent.EJE_EDAD, this.resultado))
      .map(valor => ({ rango: PerfilComponent.rangoEdad(valor.value), count: valor.count }))
      .filter((b): b is { rango: RangoEdad; count: number } => b.rango !== null);

    const colaboradores = PerfilComponent.GENERACIONES.map(cohorte => {
      const edades = PerfilComponent.edadesDeLaCohorte(cohorte, anio);

      return buckets.reduce((suma, bucket) => {
        const fraccion = PerfilComponent.aniosEnComun(bucket.rango, edades) / PerfilComponent.anios(bucket.rango);
        return suma + bucket.count * fraccion;
      }, 0);
    });

    return {
      labels: PerfilComponent.GENERACIONES.map(c => this.translate.instant('cues.perfil-clima.' + c.clave)),
      data: [{
        name: this.translate.instant('cues.perfil-clima.titulo-generacion'),
        // El reparto por fracción da decimales; se redondea porque son personas
        data: colaboradores.map(n => Math.round(n)),
      }],
      colores: PerfilComponent.GENERACIONES.map((_, i) => PerfilComponent.PALETA[i % PerfilComponent.PALETA.length]),
    };
  }

  /** Valor con mayor conteo de un eje, con su % sobre el total (ignora placeholders) */
  private valorMayoritario(valores: ValorEje[]): { name: string; pct: number } | null {
    const filtrados = this.sinPlaceholders(valores);
    if (!filtrados.length) return null;
    const total = filtrados.reduce((a, v) => a + v.count, 0);
    const top = filtrados.reduce((a, b) => (b.count > a.count ? b : a));
    return { name: top.value, pct: total > 0 ? Math.round(top.count / total * 100) : 0 };
  }

  /** Punto medio de un rango textual ("18 - 25", "1 a 3 años", "< 1 año", "+12 años", "66+") */
  private static puntoMedioRango(valor: string): number | null {
    const v = (valor || '').trim();

    let m = v.match(/^[<]\s*(\d+(?:\.\d+)?)/);
    if (m) return Number(m[1]) / 2;

    m = v.match(/(\d+(?:\.\d+)?)\s*\+\s*$/) || v.match(/^\+\s*(\d+(?:\.\d+)?)/);
    if (m) return Number(m[1]) + 5;

    m = v.match(/(\d+(?:\.\d+)?)\s*(?:-|a)\s*(\d+(?:\.\d+)?)/i);
    if (m) return (Number(m[1]) + Number(m[2])) / 2;

    return null;
  }

  /** Promedio ponderado (por count) de los puntos medios de los rangos de un eje */
  private promedioRango(valores: ValorEje[]): number | null {
    const datos = valores
      .map(v => ({ mid: PerfilComponent.puntoMedioRango(v.value), count: v.count }))
      .filter((d): d is { mid: number; count: number } => d.mid != null && d.count > 0);
    const total = datos.reduce((a, d) => a + d.count, 0);
    if (!total) return null;
    const suma = datos.reduce((a, d) => a + d.mid * d.count, 0);
    return Number((suma / total).toFixed(1));
  }

  private construirCards(): void {
    if (!this.resultado) {
      this.cardUniverso = 0;
      this.cardMuestra = 0;
      this.cardMuestraPct = 0;
      this.cardPromedioEdad = null;
      this.cardPromedioAntiguedad = null;
      this.cardFormacion = null;
      this.cardJerarquia = null;
      this.cardSucursal = null;
      this.cardGenero = null;
      return;
    }

    // Del total, no de sumar las rebanadas visibles: sinPlaceholders() esconde las hojas sin dato
    this.cardMuestra = this.resultado.totalResponses ?? 0;

    // Universo = encuestas enviadas del periodo (sin filtrar), para que no se
    // encoja al filtrar y la muestra se lea contra una base fija
    const periodo = this.periodosPropios.find(p => p.allocationId === this.allocationSelect);
    this.cardUniverso = this.resultadosService.universoDe(periodo, this.resultadoBase?.totalResponses ?? 0);
    this.cardMuestraPct = this.cardUniverso > 0 ? Math.round(this.cardMuestra / this.cardUniverso * 100) : 0;

    // Las cards describen la población ya filtrada: van con TODOS los filtros
    const res = this.resultado;
    this.cardPromedioEdad = this.promedioRango(this.valoresEje(PerfilComponent.EJE_EDAD, res));
    this.cardPromedioAntiguedad = this.promedioRango(this.valoresEje(PerfilComponent.EJE_ANTIGUEDAD, res));
    this.cardFormacion = this.valorMayoritario(this.valoresEje(PerfilComponent.EJE_FORMACION, res));
    this.cardJerarquia = this.valorMayoritario(this.valoresEje(PerfilComponent.EJE_JERARQUIA, res));
    this.cardGenero = this.valorMayoritario(this.valoresEje(PerfilComponent.EJE_GENERO, res));
    this.cardSucursal = this.valorMayoritario(this.valoresEje(PerfilComponent.EJE_SUCURSAL, res));
  }

  private recomputar(): void {
    const genero = this.construirDonut(PerfilComponent.EJE_GENERO);
    this.labelsGenero = genero.labels; this.dataGenero = genero.data; this.coloresGenero = genero.colores;

    const edad = this.construirDonut(PerfilComponent.EJE_EDAD);
    this.labelsEdad = edad.labels; this.dataEdad = edad.data; this.coloresEdad = edad.colores;

    const generacion = this.construirGeneracion();
    this.labelsGeneracion = generacion.labels; this.dataGeneracion = generacion.data; this.coloresGeneracion = generacion.colores;

    const antiguedad = this.construirSerieUnica(PerfilComponent.EJE_ANTIGUEDAD);
    this.labelsAntiguedad = antiguedad.labels; this.dataAntiguedad = antiguedad.data; this.coloresAntiguedad = antiguedad.colores;

    const jerarquia = this.construirCombo(PerfilComponent.EJE_JERARQUIA);
    this.labelsJerarquia = jerarquia.labels; this.dataJerarquia = jerarquia.data;

    const formacion = this.construirSerieUnica(PerfilComponent.EJE_FORMACION);
    this.labelsFormacion = formacion.labels; this.dataFormacion = formacion.data; this.coloresFormacion = formacion.colores;

    this.construirCards();

    // Después de las cards: el pastel se arma con cardMuestra y cardUniverso
    const universo = this.construirUniverso();
    this.labelsUniverso = universo.labels; this.dataUniverso = universo.data; this.coloresUniverso = universo.colores;

    this.armarDatosZonas();
  }

  /** Arma, una vez por recálculo, lo que cada gráfica necesita en cualquiera de sus tipos y su tabla */
  private armarDatosZonas(): void {
    const colaboradores = [this.translate.instant('cues.perfil-clima.serie-colaboradores')];
    const zona = (labels: string[], data: any[], colores: string[]): DatosZona => ({ labels, simple: { data, colores }, axisY: colaboradores });

    this.datosPorZona = [];
    this.datosPorZona[1] = zona(this.labelsUniverso, this.dataUniverso, this.coloresUniverso);
    this.datosPorZona[2] = zona(this.labelsGenero, this.dataGenero, this.coloresGenero);
    this.datosPorZona[3] = zona(this.labelsEdad, this.dataEdad, this.coloresEdad);
    this.datosPorZona[4] = zona(this.labelsGeneracion, this.dataGeneracion, this.coloresGeneracion);
    this.datosPorZona[5] = zona(this.labelsAntiguedad, this.dataAntiguedad, this.coloresAntiguedad);
    this.datosPorZona[7] = zona(this.labelsFormacion, this.dataFormacion, this.coloresFormacion);

    // Jerarquía: la combinada lleva sus dos series; en los demás tipos solo los colaboradores
    const jerarquia = zona(this.labelsJerarquia, this.dataJerarquia.slice(0, 1), this.labelsJerarquia.map((_, i) => PerfilComponent.PALETA[i % PerfilComponent.PALETA.length]));
    jerarquia.combo = { data: this.dataJerarquia, colores: this.coloresCombo };
    jerarquia.axisYCombo = [this.translate.instant('cues.perfil-clima.serie-colaboradores'), this.translate.instant('cues.perfil-clima.serie-participacion')];
    this.datosPorZona[6] = jerarquia;

    this.tablaPorZona = [];
    this.datosPorZona.forEach((datos, i) => {
      const valores: number[] = datos.simple.data[0]?.data ?? [];
      const total = valores.reduce((suma, v) => suma + Number(v), 0);
      this.tablaPorZona[i] = {
        name: datos.simple.data[0]?.name ?? '',
        valores,
        porcentaje: valores.map(v => total > 0 ? Number((Number(v) / total * 100).toFixed(1)) : 0),
        total,
      };
    });
  }

  /** Datos de la zona para el tipo que muestra la gráfica */
  public datosDe(grafico: GraficoPerfil): { labels: string[]; data: any[]; colores: string[]; axisY: string[] } {
    const zona = this.datosPorZona[grafico.zona] ?? this.datosVacios;
    const combo = grafico.type === typesApex.COMBO && !!zona.combo;
    const base = combo ? zona.combo! : zona.simple;
    const colores = grafico.type === typesApex.LINE ? this.coloresLinea : base.colores;
    return { labels: zona.labels, data: base.data, colores, axisY: combo ? zona.axisYCombo! : zona.axisY };
  }
  public labelsDe(zona: number): string[] {
    return (this.datosPorZona[zona] ?? this.datosVacios).labels;
  }
  public tablaDe(zona: number): TablaPerfil | undefined {
    return this.tablaPorZona[zona];
  }
  /** Barras, línea y embudo muestran el conteo como número; pie, mapa de calor y combinada llevan su propio formato */
  public formatoEtiquetas(grafico: GraficoPerfil): boolean {
    return [typesApex.BAR, typesApex.LINE, typesApex.FUNNEL].includes(grafico.type as typesApex);
  }
  public distribuida(grafico: GraficoPerfil): boolean {
    return [typesApex.BAR, typesApex.FUNNEL, typesApex.HEATMAP].includes(grafico.type as typesApex);
  }
  public clickGraf(grafico: GraficoPerfil, event: any): void {
    if (grafico.eje) this.clickGrafica(grafico.eje, event);
  }
  public cambiarTipo(grafico: GraficoPerfil, type: string): void {
    grafico.type = type;
  }
  public abrirModal(grafico: GraficoPerfil): void {
    grafico.ampliado = true;
    if (grafico.type !== typesApex.TABLE) {
      // Letra más grande en la vista ampliada, con lo que ya trae el graficador
      setTimeout(() => this.grafComp.find(g => g.zona === grafico.zona * 10 + 2)?.changeFontSize(), 200);
    }
  }
  /** Exporta la gráfica como imagen o, en la vista de tabla, como Excel. */
  public exportarGrafico(grafico: GraficoPerfil): void {
    const titulo = this.translate.instant(grafico.titulo);
    if (grafico.type !== typesApex.TABLE) {
      this.grafComp.find(g => g.zona === grafico.zona * 10 + 1)?.exportImg(1, { titulo: '', subtitulo: '', nombreArch: titulo });
      return;
    }
    const tabla = this.tablaDe(grafico.zona);
    if (!tabla) return;
    const datos = this.labelsDe(grafico.zona).map((label, i) => [label, tabla.valores[i], tabla.porcentaje[i] / 100]);
    datos.push([this.translate.instant('cues.perfil-clima.total'), tabla.total, 1]);
    this.excel.generateExcel(
      titulo,
      ['', tabla.name, '%'],
      datos,
      titulo,
      [Aligment.LEFT, Aligment.RIGHT, Aligment.RIGHT],
      [FormatText.TEXT, FormatText.NUMBER, FormatText.PORCENTAJE]
    );
  }

  /** Filtro de Sucursal: un eje más de filtrosActivos. Bloqueado si no hay sucursales reales */
  public async sucursalChange(click: any): Promise<void> {
    if (!this.sucursalesDisponibles) return;

    // "consolidado" alterna entre sin filtro y la primera sucursal de la lista
    const nombre = click === 'consolidado'
      ? (this.filtroDe(PerfilComponent.EJE_SUCURSAL) ?? this.sucursales[0]?.name)
      : this.sucursales.find(s => s.name === click?.name)?.name;

    if (nombre) await this.alternarFiltro(PerfilComponent.EJE_SUCURSAL, nombre);
  }

  /** Filtro de Dimensión (selección única): consolidado o exactamente una dimensión */
  public dimensionChange(click: any): void {
    if (click === 'consolidado') {
      this.dimensionConsolidado = !this.dimensionConsolidado;
      if (this.dimensionConsolidado) {
        this.dimensiones.forEach(d => d.check = false);
      } else {
        this.dimensiones.forEach((d, i) => d.check = i === 0);
      }
      this.recomputar();
      return;
    }

    const dim = this.dimensiones.find(d => d.name === click?.name);
    if (!dim) return;
    const yaEstaba = dim.check;
    this.dimensiones.forEach(d => d.check = false);
    dim.check = !yaEstaba;
    this.dimensionConsolidado = !dim.check;
    this.recomputar();
  }
}
